import { useState, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { useIntl } from "react-intl";

//Components
import { Grid, Typography, Container, Paper, Divider, Icon } from "@mui/material";
import { Button, Tooltip } from "@gpstrackit/react-controls"
import AdvancedFilters from './.components/AdvancedFilters';
import { TagsGroup } from 'Components/Tag';

//EndPoints
import { endpoints } from 'Core/defaultValues';

//Views
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import ChipStatus from 'Components/ChipStatus';
import PurgeAccount from '../components/purgeAccount';
import CreateEditAccount from '../components/createEditAccount';
import AccountDetails from 'Modules/accounts/views/main/.components/details';
import ReprocessOdoHours from '../../.components/reprocessOdoHours';
import BulkTags from 'Modules/tags/.components/bulkTags';
import Activities from "Modules/activities/.components/tab";
import SyncEntities from '../components/syncEntities';
import EdgeTensor from 'Modules/accounts/.components/tabEdgeTensor';

//Helpers
import { getTagColor } from 'Core/data/Helpers';

//Security
import { HasPermission } from "Core/security";

//Notification
import { NotificationManager } from "Components/Notifications";

// hook
import { useTokenOneTimeHandler } from 'Core/hooks';

//Actions
import {
    reprocessOdoHoursOpenModal,
    accountsCreateEditAccountOpenModal,
    accountsGetDetailsAccountOpenModal,
    searchEntityRequest,
    bulkTagsOpenModal,
    activitiesSearchOpenModal,
    openModalSyncEntities,
    openModalEdgeTensor
} from 'Redux/actions';

const AccountsMain = (props) => {
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    const purgeAccountOModal = useSelector(state => state.purgeAccountRedux.openModal);
    const createEditAccountOModal = useSelector(state => state.createEditAccountRedux.openModal);
    const detailsOpenModal = useSelector(state => state.accountDetailsRedux.openModal);
    const openModalReprocessOdoHours = useSelector(state => state.reprocessOdoHoursRedux.openModal);
    const events = useSelector(state => state.generalRedux.events);
    const dealers = useSelector(state => state.generalRedux.dealers);
    const user = useSelector(state => state.securityRedux.user);
    const openModalBulkTags = useSelector(state => state.bulkTagsRedux.openModal);
    const openModalActivities = useSelector(state => state.activitiesTabRedux.openModal);
    const openModalSyncZonar = useSelector(state => state.syncEntitiesRedux.openModal);
    const edgeTensorOpenModal = useSelector(state => state.edgeTensorRedux.openModal);
    const tokenOneTime = useSelector(state => state.accountOverviewRedux.tokenOneTime);

    const { handleAuthenticate } = useTokenOneTimeHandler(tokenOneTime, dealers);

    const statusOptions = [
        {
            id: 1,
            label: messages['active']
        },
        {
            id: 4,
            label: messages['cancelled']
        },
        {
            id: 3,
            label: messages['involuntary_suspension']
        },
        {
            id: 2,
            label: messages['voluntary_suspension']
        }
    ]

    const [columns, setColumns] = useState([
        {
            key: 'view',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            render: (item) => {
                return (
                    <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                        dispatch(accountsGetDetailsAccountOpenModal(true, item.id))
                    }}>
                        remove_red_eye
                    </Icon>
                )
            }
        },
        {
            key: "id",
            title: messages['accounts_main_id'],
            width: 25,
            show: true,
            sort: true,
            locked: true,
            resize: true,
            export: true,
            functionString: 'return row?.id',
            code: "accounts_main_id",
            render: (item) => {
                return <span>{item?.id}</span>
            }
        },
        {
            key: "name",
            field: "name",
            title: messages['accounts_main_name'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "accounts_main_name",
            render: (item) => {
                let name = item?.name
                if (name?.length >= 7) {
                    name = name?.substring(0, 40) + "...";
                }
                return (
                    <NavLink to={`/app/accounts/overview/${item?.id}`} style={{ color: 'black' }}>
                        <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{name}</span>
                    </NavLink>
                )
            },
            export: true,
            functionString: 'return row?.name'
        },
        {
            key: "dealer_name",
            field: "dealer.name",
            title: messages['accounts_main_dealer'],
            width: 350,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "accounts_main_dealer",
            render: (item) => {
                return <span>{item?.dealer?.name}</span>
            },
            export: true,
            functionString: 'return row?.dealer?.name'
        },
        {
            key: "dealer_brand_title",
            field: "dealer { id brandId brand { id title } }",
            title: messages['accounts_main_brand'],
            width: 350,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "accounts_main_brand",
            render: (item) => {
                return <span>{item?.dealer?.brand?.title}</span>
            },
            export: true,
            functionString: 'return row?.dealer?.brand?.title'
        },
        {
            key: "role_name",
            field: "role { id name }",
            title: messages['accounts_main_package'],
            width: 350,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "accounts_main_package",
            render: (item) => {
                return <span>{item?.role?.name}</span>
            },
            export: true,
            functionString: 'return row?.role?.name'
        },
        {
            key: "crmId",
            field: "crmId",
            title: messages['accounts_main_crm'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "accounts_main_crm",
            render: (item) => {
                return <a
                    style={{ color: 'black' }}
                    href={`${endpoints.SALESFORCE_URL}lightning/r/Account/${item?.crmId}/view`}
                    target="_blank"
                >
                    {item?.crmId}
                </a>
            },
            export: true,
            functionString: 'return row?.crmId'
        },
        {
            key: "devicesCount",
            field: "devicesCount",
            title: messages['account_overview_active_devices'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "account_overview_active_devices",
            render: (item) => {
                return <span>{item?.devicesCount}</span>
            },
            export: true,
            functionString: 'return row?.devicesCount'
        },
        {
            key: "status",
            field: "status",
            title: messages['accounts_main_status'],
            sort: true,
            width: 200,
            show: true,
            locked: false,
            resize: true,
            code: "accounts_main_status",
            render: (item) => {
                let nextScheduledAction = item?.nextScheduledAction
                let message = null
                if (nextScheduledAction) {
                    try {
                        let data = JSON.parse(nextScheduledAction)
                        message = `${messages['accounts_main_next_scheduled_action']}:`
                        if (data && data?.action === "statusUpdate") {
                            let status = statusOptions.find(item => item?.id === data?.data?.newStatusId)
                            if (data?.entityName === "Account") {
                                message = `${messages[data?.action] || data?.action}${status ? ` ${messages[status?.label] || status?.label}` : ''} ${messages['accounts_main_at']} ${data?.scheduleOn}`
                            } else if (data?.entityName === "Unit") {
                                message = `${messages[data?.action] || data?.action}${status ? ` ${messages[status?.label] || status?.label}` : ''} ${messages['accounts_main_on']} ${messages['accounts_main_devices']} ${messages['accounts_main_at']} ${data?.scheduleOn}`
                            }
                        }
                    } catch (exc) { }
                }
                return (
                    <div style={{ display: 'flex' }}>
                        <ChipStatus statusCode={item?.status} messages={messages} />
                        {nextScheduledAction &&
                            <div style={{ marginLeft: 5 }}>
                                <Tooltip title={message}>
                                    <Icon style={{ color: '#1664f5', cursor: 'pointer' }}>access_alarms</Icon>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            },
            export: true,
            functionString: 'return `${row?.status === 1 ? "Active" : row?.status === 2 ?  "Voluntary Suspension" : row?.status === 3 ?  "Involuntary Suspension" : row?.status === 4 ?  "Cancelled" : row?.status === 6 ?  "RMA" : "Desactivated" }`;'
        },
        {
            key: "tags",
            field: "tags",
            title: messages['accounts_main_tags'],
            width: 350,
            show: true,
            locked: false,
            resize: true,
            code: "accounts_main_tags",
            render: (item) => {
                let unitTags = [];
                let items = item?.tags ? JSON.parse(item?.tags) : [];
                if (items) {
                    unitTags = items?.map(t => {
                        return { ...t, color: getTagColor(t?.color) };
                    });
                }

                unitTags = unitTags?.filter(unitTag => unitTag?.id != -1);

                return (
                    <TagsGroup
                        options={unitTags}
                        nameField="label"
                        colorField="color"
                    />
                )
            },
            export: true,
            functionString: 'return row?.tags?.map(x => x.label).join(",\r\n")'
        },
        {
            key: "registrationStatus",
            field: "registrationStatus",
            title: messages['account_overview_registration_status'],
            width: 200,
            show: true,
            sort: false,
            locked: false,
            resize: true,
            code: "account_overview_registration_status",
            render: (item) => {
                return (
                    <div style={{ width: 210, height: 25 }}>
                        <ChipStatus label={item?.registrationStatus} messages={messages} />
                    </div>
                )
            },
            export: true,
            functionString: 'return row?.registrationStatus'
        },
        {
            key: "readonly",
            field: "readonly",
            title: messages['accounts_main_readonly'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "accounts_main_readonly",
            render: (item) => {
                return (
                    <div style={{ width: 210, height: 25 }}>
                        {item?.readonly ? messages['common_yes'] : messages['common_no']}
                    </div>
                )
            },
            export: true,
            functionString: 'return row?.readonly ? "Yes" : "No"'
        }
    ])
    const [openPanel, setOpenPanel] = useState(false);
    const [tags, setTags] = useState([]);
    const [total, setTotal] = useState(0);
    const [dealer, setDealer] = useState(null);
    const [roles, setRoles] = useState(null);
    const [searchFields, setSearchFields] = useState(["name", "username", "email", "dealer_name"]);
    const [selectedTags, setSelectedTags] = useState([])
    const [selectStatusAccount, setSelectStatusAccount] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);

    const tagsHandler = (newTags) => {
        setTags(newTags?.terms || []);
    }

    useEffect(() => {
        (!events || events.length === 0) && dispatch(searchEntityRequest({ key: "events" }));
        (!dealers || dealers.length === 0) && dispatch(searchEntityRequest({ key: "dealers" }));
    }, []);

    // ============================================
    // DataViewInfiniteScroll Callback Handlers
    // (Extracted for readability - initialized in logic part)
    // ============================================

    // Default sort and limit configuration
    const [currentSort] = useState([{ field: "id", order: "ASC" }]);
    const LIMIT = 50;

    // onChangeConditions handler
    const handleChangeConditions = (tags, conditions, total, newColumns, rows, sort, key, fromPreferences, restoreOnly) => {
        // restoreOnly: data already persisted in redux, just sync UI states — skip getData
        if (restoreOnly) return;

        //current conditions
        let currentConditions = {}
        let currentTags = {}

        if (!conditions && tags) {
            currentConditions = tableRef?.current?.conditions;
            currentTags = tags;
        } else {
            currentConditions = conditions || [];
            currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
            if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);

            //advanced filters
            let validateTags = currentConditions && currentConditions?.find((tag) => tag?.fields && tag?.fields[0] === "tagIds");
            validateTags && setSelectedTags(validateTags?.terms);
            let validateStatus = currentConditions && currentConditions?.find((tag) => tag?.fields && tag?.fields[0] === "status");
            setSelectStatusAccount(Array.isArray(validateStatus?.terms) ? validateStatus.terms : []);
            let validatedealer = currentConditions && currentConditions?.find((tag) => tag?.fields && tag?.fields[0] === "dealerId");
            validatedealer?.terms && setDealer(validatedealer?.terms[0] || null);
            let validateRole = currentConditions && currentConditions?.find((tag) => tag?.fields && tag?.fields[0] === "roleId");
            validateRole?.terms && setRoles(validateRole?.terms[0] || null);
        }

        currentConditions = currentConditions?.filter((item) => item?.fields && item?.fields[0] !== 'id' && item?.fields[0] !== 'name');

        let tagsById = currentTags.filter((item) => typeof item.id === "number");
        if (tagsById?.length > 0) {
            //New Items
            let currentIds = tagsById.map((item) => (item.id));
            if (currentIds.length > 0) {
                currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
            }
        }

        let tagsByLabel = currentTags.filter((item) => typeof item.id === "string");

        if (tagsByLabel.length > 0) {
            let currentLabels = tagsByLabel.map((item) => (item.name))
            if (currentLabels.length > 0) {
                currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
            }
        }

        if (!conditions && tags) tableRef?.current?.getData({ newSort: sort, newConditions: currentConditions });
        else tableRef?.current?.getData({ newSort: sort, newRows: rows, newConditions: currentConditions, newColumns });
    };

    // onChangeColumns handler
    const handleChangeColumns = (items) => {
        let currentColumns = items.map((item) => ({
            ...item,
            title: item.code ? messages[item.code] : item.title,
            render: item.key === "status" ? (item) => {
                let nextScheduledAction = item?.nextScheduledAction
                let message = null
                if (nextScheduledAction) {
                    try {
                        let data = JSON.parse(nextScheduledAction)
                        message = `${messages['accounts_main_next_scheduled_action']}:`
                        if (data && data?.action === "statusUpdate") {
                            let status = statusOptions.find(item => item?.id === data?.data?.newStatusId)
                            if (data?.entityName === "Account") {
                                message = `${messages[data?.action] || data?.action}${status ? ` ${messages[status?.label] || status?.label}` : ''} ${messages['accounts_main_at']} ${data?.scheduleOn}`
                            } else if (data?.entityName === "Unit") {
                                message = `${messages[data?.action] || data?.action}${status ? ` ${messages[status?.label] || status?.label}` : ''} ${messages['accounts_main_on']} ${messages['accounts_main_devices']} ${messages['accounts_main_at']} ${data?.scheduleOn}`
                            }
                        }
                    } catch (exc) { }
                }
                return (
                    <div style={{ display: 'flex' }}>
                        <ChipStatus statusCode={item?.status} messages={messages} />
                        {nextScheduledAction &&
                            <div style={{ marginLeft: 5 }}>
                                <Tooltip title={message}>
                                    <Icon style={{ color: '#1664f5', cursor: 'pointer' }}>access_alarms</Icon>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            } : item.render,
        }))
        setColumns(currentColumns)
    };

    // options array for DataViewInfiniteScroll
    const tableOptions = [
        {
            id: "accounts_main_authenticate_cloud",
            label: messages['accounts_main_authenticate_cloud'],
            single: true,
            show: true,
            disabled: !HasPermission(user, "cmplc"),
            action: (items, event) => {
                let currentItem = items.find(item => item.id);
                handleAuthenticate(event, currentItem?.dealer?.id, currentItem?.email || currentItem?.username, `PX_${currentItem?.id}`);
            }
        },
        {
            id: "accounts_main_edit",
            label: messages['accounts_main_edit'],
            single: true,
            show: true,
            disabled: !HasPermission(user, "cmp", "u"),
            action: (items) => {
                let currentItem = items.find(item => item.id);
                dispatch(accountsCreateEditAccountOpenModal(currentItem))
            }
        },
        {
            id: "accounts_main_reprocess_odo_hours",
            label: messages['accounts_main_reprocess_odo_hours'],
            single: true,
            show: true,
            disabled: !HasPermission(user, "rprcssoh", "c"),
            action: (items) => {
                let currentItem = items.find(item => item.id);
                dispatch(reprocessOdoHoursOpenModal({ selectedAccount: currentItem }))
            }
        },
        {
            id: "bulk-tags",
            label: messages['devices_main_bulk_tags'],
            show: true,
            disabled: !HasPermission(user, "assgntgs", "c"),
            multiple: true,
            action: () => {
                if (selectedItems.length > 0) {
                    dispatch(bulkTagsOpenModal(selectedItems))
                } else {
                    NotificationManager.warning(
                        messages['devices_main_select_devices'],
                        messages['devices_main_bulk_tags'],
                        5000
                    );
                }
            }
        },
        {
            id: "sync-entities",
            label: messages['accounts_main_syncZonar'],
            single: true,
            show: true,
            disabled: !HasPermission(user, "synczon", "u"),
            action: (items) => {
                let currentItem = items.find(item => item.id);
                dispatch(openModalSyncEntities({ account: currentItem }));
            }
        },
    ];

    // menuOptions function
    const getMenuOptions = () => {
        return [
            messages['accounts_main_authenticate_cloud'],
            messages['accounts_main_edit'],
            messages['accounts_main_reprocess_odo_hours'],
            messages['accounts_main_syncZonar'],
            messages['accounts_main_edge_tensor']
        ]
    };

    // onClickMenu handler
    const handleClickMenu = (event, item, clickEvent) => {
        if (event === messages['accounts_main_authenticate_cloud']) {
            if (HasPermission(user, "cmplc")) {
                handleAuthenticate(clickEvent, item?.dealer?.id, item?.email || item?.username, `PX_${item?.id}`)
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['accounts_main_authenticate_cloud'],
                    5000,
                );
            }
        }
        if (event === messages['accounts_main_edit']) {
            if (HasPermission(user, "cmp", "u")) {
                dispatch(accountsCreateEditAccountOpenModal(item))
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['accounts_main_edit'],
                    5000,
                );
            }
        }
        if (event === messages['accounts_main_reprocess_odo_hours']) {
            if (HasPermission(user, "rprcssoh", "c")) {
                dispatch(reprocessOdoHoursOpenModal({ selectedAccount: item }))
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['accounts_main_edit'],
                    5000,
                );
            }
        }
        if (event === messages['accounts_main_syncZonar']) {
            if (HasPermission(user, "synczon", "u")) {
                if (item?.integrations?.length) {
                    let zonar = item?.integrations?.some(integration => integration?.integration?.code === "zonar");
                    if (zonar) {
                        dispatch(openModalSyncEntities({ account: item }))
                    } else {
                        NotificationManager.warning(
                            messages['accounts_main_error_integration_zonar'],
                            messages['accounts_main_syncZonar'],
                            5000,
                        );
                    }
                } else {
                    NotificationManager.warning(
                        messages['accounts_main_error_integration_zonar'],
                        messages['accounts_main_syncZonar'],
                        5000,
                    );
                }
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['accounts_main_syncZonar'],
                    5000,
                );
            }
        }
        if (event === messages['accounts_main_edge_tensor']) {
            if (HasPermission(user, "edgtnsr")) {
                dispatch(openModalEdgeTensor({ open: true, account: item }));
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['accounts_main_edge_tensor'],
                    5000,
                );
            }
        }
    };

    // customColumn definition
    const customColumnConfig = {
        render: (row) => {
            return (
                <div>
                    <Tooltip title={messages['users_tab_show_activities']}>
                        <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                            if (HasPermission(user, "act")) {
                                dispatch(activitiesSearchOpenModal({ entityName: "Company", entityId: row.id }, "accounts-main-modal", row.id))
                            } else {
                                NotificationManager.warning(
                                    messages['common_error_permission'],
                                    messages['users_tab_users'],
                                    5000,
                                );
                            }
                        }} >
                            assignment
                        </Icon>
                    </Tooltip>
                </div>
            )
        }
    };

    let innerHeight = window.innerHeight

    return (
        <Container maxWidth={false} className="container-view">
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    <Grid item xs={6}>
                        <Typography variant="h5" gutterBottom component="div">
                            {messages['accounts_main_accounts']} {/* ({total}) */}
                        </Typography>
                    </Grid>
                    <Grid item xs={6} className="main-view-buttons" style={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                            disabled={!HasPermission(user, "cmp", "c")}
                            className="btn-dark-blue"
                            onClick={(e) => {
                                e.preventDefault();
                                dispatch(accountsCreateEditAccountOpenModal())
                            }}
                        >
                            {messages['accounts_main_add_account']}
                        </Button>
                    </Grid>
                    <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                        <Divider style={{ backgroundColor: '#999999' }} />
                    </div>
                    <DataViewInfiniteScroll
                        key="pm_main_accounts_list"
                        ref={tableRef}
                        clearOnUnmount={false}
                        
                        // Core props (remain flat)
                        name="pm-accounts"
                        columns={columns}
                        infiniteScroll
                        entity="companies"
                        
                        graphqlConfig={{
                            function: "searchCompanies",
                            sortType: "CompanySortInput",
                            conditionsType: "CompanySearchInput",
                            dynamicQuery: true,
                            customQuery: "id sm nextScheduledAction username email dealer { id } integrations { id integrationId integration { code } }",
                        }}

                        searchConfig={{
                            conditions: ["name", "username", "email"],
                            optionValue: "id",
                            optionLabel: "name",
                        }}

                        exportConfig={{
                            fileName: "accounts",
                        }}
                        
                        uiConfig={{
                            topHeight: 200,
                            filterSelected: (dealer || roles || selectStatusAccount?.length > 0 || selectedTags?.length > 0),
                        }}

                        defaultsConfig={{
                            sort: currentSort,
                            rowsPerPage: LIMIT,
                        }}

                        onFilters={() => { setOpenPanel(!openPanel) }}
                        onChangeColumns={handleChangeColumns}
                        onChangeConditions={handleChangeConditions}
                        onSelectedItems={(items) => setSelectedItems(items)}
                        
                        options={tableOptions}
                        menuOptions={getMenuOptions}
                        onClickMenu={handleClickMenu}
                        customColumn={customColumnConfig}
                        tableButtons={["export", 'orderColumn']}
                    />
                </Grid>
            </Paper>
            <AdvancedFilters
                setOpenPanel={setOpenPanel}
                openPanel={openPanel}
                tableRef={tableRef}
                currentTags={tags}
                setTags={setTags}
                setDealer={setDealer}
                dealer={dealer}
                currentRoles={roles}
                setRoles={setRoles}
                selectedTags={selectedTags}
                setSelectedTags={setSelectedTags}
                selectStatusAccount={selectStatusAccount}
                setSelectStatusAccount={setSelectStatusAccount}
                statusOptions={statusOptions}
            />
            {purgeAccountOModal && <PurgeAccount />}
            {createEditAccountOModal && <CreateEditAccount tableRef={tableRef} />}
            {detailsOpenModal && <AccountDetails />}
            {openModalReprocessOdoHours && <ReprocessOdoHours />}
            {openModalBulkTags && <BulkTags entityName={"Company"} tableRef={tableRef} />}
            {openModalActivities && <Activities noFilters2 noOpenSearch2 modalView noUsers height={innerHeight - 257} entity={"activitiesMainAccounts"} instanceId="accounts-main-modal" />}
            {openModalSyncZonar && <SyncEntities />}
            {edgeTensorOpenModal && <EdgeTensor height={innerHeight - 257} mode="modal" />}
        </Container>
    )
}
export default AccountsMain;